import React, {useEffect, useState} from 'react';
import { Link } from 'react-router-dom';
import { getFarmById, getFarmInspectionsByFarmId } from "../../api/web";
import moment from "moment";

const FarmDetail = (props) => {

    const id = props.match.params.id;
    const farmId = parseInt(id, 10);
    const [initialLoading, setInitialLoading] = useState(true);
    const [farm, setFarm] = useState(null);
    const [inspections, setInspections] = useState([]);
    const [inspectionsLoading, setInspectionsLoading] = useState(false);

    useEffect(() => {
        getFarmById(id).then((response) => {
            if (!response.error) {
                setFarm(response.payload);
            }
            setInitialLoading(false);
        });
    }, [id]);

    useEffect(() => {
        if (!farm || !farmId) return;
        setInspectionsLoading(true);
        getFarmInspectionsByFarmId(farmId).then((res) => {
            if (!res.error && Array.isArray(res.payload)) {
                setInspections(res.payload);
            } else {
                setInspections([]);
            }
            setInspectionsLoading(false);
        });
    }, [farm, farmId]);

    if (initialLoading) {
        return (
            <div className="content-body">
                <div className="container d-flex justify-content-center ht-100p">
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p className="mg-t-10">Loading farm</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!farm) {
        return (
            <div className="content-body">
                <div className="container pd-x-0">
                    <div className="mg-b-20">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/farm">Farms</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Not found</li>
                            </ol>
                        </nav>
                        <p className="tx-danger">Farm not found.</p>
                        <Link to="/farm" className="btn btn-sm btn-primary">Back to Farms</Link>
                    </div>
                </div>
            </div>
        );
    }

    const farmerName = farm.farmer
        ? `${farm.farmer.first_name || ''} ${farm.farmer.last_name || ''}`.trim() || '—'
        : '—';
    const provinceName = farm.province?.name ?? '—';
    const nearestTownName = farm.nearest_town?.name ?? '—';
    const farmComponents = farm.farm_component || [];

    return (
        <div className="content-body">
            <div className="container pd-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/farm">Farms</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Farm #{farm.id}</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-5 tx-spacing--1">Farm #{farm.id}</h4>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <span className="tx-color-03 tx-13">{farm.name ?? '—'} · {farmerName}</span>
                        </div>
                    </div>
                    <div className="d-none d-md-block">
                        <Link to="/farm" className="btn btn-sm pd-x-15 btn-secondary btn-uppercase">
                            <i className="wd-10 mg-r-5 fa fa-arrow-left"></i> Back to list
                        </Link>
                    </div>
                </div>

                <div className="row row-xs">
                    <div className="col-md-12 mg-t-10">
                        <div className="nav-wrapper mg-b-20 tx-13">
                            <ul className="nav nav-line nav-fill tx-medium">
                                <li className="nav-item">
                                    <a href="#info" className="nav-link active" data-bs-toggle="tab">
                                        <i className="ion-md-information-circle"/> Information
                                    </a>
                                </li>
                                {farmComponents.length > 0 && (
                                    <li className="nav-item">
                                        <a href="#components" className="nav-link" data-bs-toggle="tab">
                                            <i className="ion-md-list"/> Farm Components ({farmComponents.length})
                                        </a>
                                    </li>
                                )}
                                <li className="nav-item">
                                    <a href="#inspections" className="nav-link" data-bs-toggle="tab">
                                        <i className="ion-md-clipboard"/> Farm Inspections ({inspections.length})
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div className="tab-content">
                            <div id="info" className="tab-pane fade active show">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="row row-sm">
                                            <div className="col-md-6">
                                                <p><strong>Name:</strong> {farm.name ?? '—'}</p>
                                                <p><strong>Farmer:</strong> {farmerName}</p>
                                                <p><strong>Province:</strong> {provinceName}</p>
                                                <p><strong>Nearest town:</strong> {nearestTownName}</p>
                                                <p><strong>Area:</strong> {farm.area != null ? farm.area : '—'}</p>
                                                <p><strong>Length:</strong> {farm.length != null ? farm.length : '—'}</p>
                                                <p><strong>Width:</strong> {farm.width != null ? farm.width : '—'}</p>
                                                <p><strong>Tonnage per hectare:</strong> {farm.tonnage_per_hectare != null ? farm.tonnage_per_hectare : '—'}</p>
                                            </div>
                                            <div className="col-md-6">
                                                <p><strong>Latitude:</strong> {farm.latitude != null ? farm.latitude : '—'}</p>
                                                <p><strong>Longitude:</strong> {farm.longitude != null ? farm.longitude : '—'}</p>
                                                <p><strong>Farming camp:</strong> {farm.farming_camp?.name ?? '—'}</p>
                                                <p><strong>Farming block:</strong> {farm.farming_block?.name ?? '—'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {farmComponents.length > 0 && (
                                <div id="components" className="tab-pane fade">
                                    <div className="card">
                                        <div className="card-header">
                                            <h6 className="mg-b-0">Farm Components</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Name / Type</th>
                                                            <th>Details</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {farmComponents.map((item, idx) => (
                                                            <tr key={idx}>
                                                                <td>{typeof item === 'object' ? (item.name ?? item.component_name ?? '—') : String(item)}</td>
                                                                <td>{typeof item === 'object' ? JSON.stringify(item).slice(0, 80) : '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div id="inspections" className="tab-pane fade">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Farm Inspections</h6>
                                    </div>
                                    <div className="card-body">
                                        {inspectionsLoading ? (
                                            <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span className="mg-l-10 tx-13">Loading inspections...</span>
                                            </div>
                                        ) : inspections.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No farm inspections for this farm.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Title</th>
                                                            <th>Season</th>
                                                            <th>Active</th>
                                                            <th>Verified</th>
                                                            <th>Date Created</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {inspections.map((insp) => (
                                                            <tr key={insp.id}>
                                                                <td>{insp.title ?? '—'}</td>
                                                                <td>{insp.season ?? '—'}</td>
                                                                <td>{insp.active ? 'Yes' : 'No'}</td>
                                                                <td>{insp.verified ? 'Yes' : 'No'}</td>
                                                                <td>{insp.date_created ? moment(insp.date_created).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                                <td>
                                                                    <Link to={`/farm_inspection/${insp.id}`} className="nav-link d-inline">
                                                                        <i className="fa fa-eye"></i> View inspection
                                                                    </Link>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FarmDetail;
